import { createSlice } from '@reduxjs/toolkit';
import {
  fetchR2Buckets,
  fetchR2Files,
  deleteR2File,
  renameR2File,
  createR2Folder,
  renameR2Folder,
  deleteR2Folder
} from "../../actions/watcher/r2FilesActions";

export interface R2ObjectInterface {
  key: string;
  filename: string;
  size: number;
  last_modified: string;
  etag: string;
  url: string | null;
}

export interface R2FolderInterface {
  name: string;
  prefix: string;
}

export interface R2BucketInterface {
  id: string;
  label: string;
}

interface R2FilesState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  objects: R2ObjectInterface[];
  folders: R2FolderInterface[];
  prefix: string;
  buckets: R2BucketInterface[];
  activeBucketId: string | null;
  nextContinuationToken: string | null;
  isTruncated: boolean;
}

const initialState: R2FilesState = {
  status: 'idle',
  error: null,
  objects: [],
  folders: [],
  prefix: '',
  buckets: [],
  activeBucketId: null,
  nextContinuationToken: null,
  isTruncated: false,
};

const slice = createSlice({
  name: 'r2Files',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // #buckets
      .addCase(fetchR2Buckets.fulfilled, (state, action) => {
        state.buckets = action.payload.buckets;
      })

      // #index
      .addCase(fetchR2Files.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchR2Files.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.objects = action.payload.objects;
        state.folders = action.payload.folders;
        state.prefix = action.payload.prefix;
        state.activeBucketId = action.payload.bucket_id || null;
        state.nextContinuationToken = action.payload.next_continuation_token;
        state.isTruncated = action.payload.is_truncated;
      })
      .addCase(fetchR2Files.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to load files';
      })

      // #deleteFile
      .addCase(deleteR2File.fulfilled, (state, action) => {
        state.objects = state.objects.filter(o => o.key !== action.payload.key);
      })

      // #renameFile
      .addCase(renameR2File.fulfilled, (state, action) => {
        const idx = state.objects.findIndex(o => o.key === action.payload.old_key);
        if (idx !== -1) {
          state.objects[idx] = {
            ...state.objects[idx],
            key: action.payload.object.key,
            filename: action.payload.object.filename,
            url: action.payload.object.url
          };
        }
      })

      // #createFolder
      .addCase(createR2Folder.fulfilled, (state, action) => {
        state.folders.push(action.payload.folder);
      })

      // #renameFolder
      .addCase(renameR2Folder.fulfilled, (state, action) => {
        const idx = state.folders.findIndex(f => f.prefix === action.payload.old_prefix);
        if (idx !== -1) {
          state.folders[idx] = action.payload.folder;
        }
      })

      // #deleteFolder
      .addCase(deleteR2Folder.fulfilled, (state, action) => {
        state.folders = state.folders.filter(f => f.prefix !== action.payload.prefix);
      })
  }
});

const reducer = slice.reducer;
export default reducer;
